import type { JSONContent } from '@tiptap/react';

/**
 * Convert TipTap/ProseMirror JSON content to Markdown.
 */
export function tiptapToMarkdown(content: JSONContent): string {
  if (!content || !content.content) return '';
  return content.content.map(node => nodeToMarkdown(node)).join('\n');
}

function nodeToMarkdown(node: JSONContent, listDepth = 0): string {
  const indent = '  '.repeat(listDepth);

  switch (node.type) {
    case 'paragraph': {
      const pIndent = '    '.repeat(node.attrs?.indent || 0);
      return pIndent + inlineToMarkdown(node.content) + '\n';
    }

    case 'heading': {
      const level = node.attrs?.level ?? 1;
      const hashes = '#'.repeat(level);
      const hIndent = '    '.repeat(node.attrs?.indent || 0);
      return `${hIndent}${hashes} ${inlineToMarkdown(node.content)}\n`;
    }

    case 'bulletList':
      return (node.content ?? [])
        .map(item => listItemToMarkdown(item, listDepth, '-'))
        .join('\n') + '\n';

    case 'orderedList':
      return (node.content ?? [])
        .map((item, i) => listItemToMarkdown(item, listDepth, `${i + 1}.`))
        .join('\n') + '\n';

    case 'taskList':
      return (node.content ?? [])
        .map(item => {
          const checked = item.attrs?.checked ? 'x' : ' ';
          const text = inlineFromListItem(item);
          return `${indent}- [${checked}] ${text}`;
        })
        .join('\n') + '\n';

    case 'listItem': {
      return `${indent}${inlineFromListItem(node)}`;
    }

    case 'codeBlock': {
      const lang = node.attrs?.language ?? '';
      const code = (node.content ?? []).map(c => c.text ?? '').join('');
      return `\`\`\`${lang}\n${code}\n\`\`\`\n`;
    }

    case 'blockquote':
      return (node.content ?? [])
        .map(child => '> ' + nodeToMarkdown(child).trimEnd())
        .join('\n') + '\n';

    case 'table':
      return tableToMarkdown(node) + '\n';

    case 'horizontalRule':
      return '---\n';

    case 'image': {
      const alt = node.attrs?.alt ?? '';
      const src = node.attrs?.src ?? '';
      return `![${alt}](${src})\n`;
    }

    case 'details': {
      const summary = node.attrs?.summary ?? 'Details';
      const inner = (node.content ?? []).map(n => nodeToMarkdown(n)).join('\n');
      return `<details>\n<summary>${summary}</summary>\n\n${inner}\n</details>\n`;
    }

    case 'hardBreak':
      return '\n';

    default:
      // Unknown node type — try to extract text
      if (node.content) {
        return node.content.map(n => nodeToMarkdown(n, listDepth)).join('');
      }
      return node.text ?? '';
  }
}

function listItemToMarkdown(item: JSONContent, depth: number, bullet: string): string {
  const indent = '  '.repeat(depth);
  const text = inlineFromListItem(item);
  // Check for nested lists
  const nested = (item.content ?? [])
    .filter(c => c.type === 'bulletList' || c.type === 'orderedList' || c.type === 'taskList')
    .map(c => nodeToMarkdown(c, depth + 1))
    .join('');
  return `${indent}${bullet} ${text}${nested ? '\n' + nested.trimEnd() : ''}`;
}

function inlineFromListItem(item: JSONContent): string {
  const paragraphs = (item.content ?? []).filter(
    c => c.type === 'paragraph' || c.type === 'text'
  );
  return paragraphs.map(p => inlineToMarkdown(p.content ?? (p.text ? [p] : []))).join(' ').trim();
}

function inlineToMarkdown(content?: JSONContent[]): string {
  if (!content) return '';
  return content.map(node => {
    let text = node.text ?? '';

    // Handle special inline node types
    if (node.type === 'mention') {
      return `@${node.attrs?.label ?? node.attrs?.id ?? ''}`;
    }
    if (node.type === 'entityLink') {
      return `[[${node.attrs?.label ?? node.attrs?.name ?? ''}]]`;
    }
    if (node.type === 'hardBreak') {
      return '\n';
    }
    if (node.type === 'image') {
      return `![${node.attrs?.alt ?? ''}](${node.attrs?.src ?? ''})`;
    }

    // Apply marks
    if (node.marks) {
      for (const mark of node.marks) {
        switch (mark.type) {
          case 'bold':
          case 'strong':
            text = `**${text}**`;
            break;
          case 'italic':
          case 'em':
            text = `*${text}*`;
            break;
          case 'strike':
            text = `~~${text}~~`;
            break;
          case 'code':
            text = `\`${text}\``;
            break;
          case 'link':
            text = `[${text}](${mark.attrs?.href ?? ''})`;
            break;
          case 'highlight':
            text = `==${text}==`;
            break;
        }
      }
    }

    return text;
  }).join('');
}

function tableToMarkdown(node: JSONContent): string {
  const rows = node.content ?? [];
  if (rows.length === 0) return '';

  const tableData: string[][] = rows.map(row =>
    (row.content ?? []).map(cell => inlineToMarkdown(cell.content?.[0]?.content))
  );

  if (tableData.length === 0) return '';

  const colCount = Math.max(...tableData.map(r => r.length));
  const colWidths = Array.from({ length: colCount }, (_, i) =>
    Math.max(3, ...tableData.map(row => (row[i] ?? '').length))
  );

  const lines: string[] = [];
  for (let r = 0; r < tableData.length; r++) {
    const cells = Array.from({ length: colCount }, (_, c) =>
      (tableData[r][c] ?? '').padEnd(colWidths[c])
    );
    lines.push('| ' + cells.join(' | ') + ' |');
    if (r === 0) {
      lines.push('| ' + colWidths.map(w => '-'.repeat(w)).join(' | ') + ' |');
    }
  }

  return lines.join('\n');
}

/**
 * Download a string as a Markdown file.
 */
export function downloadMarkdown(content: string, filename: string): void {
  const blob = new Blob([content], { type: 'text/markdown;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename.endsWith('.md') ? filename : `${filename}.md`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Trigger the browser print dialog (which offers "Save as PDF").
 */
export function printAsPdf(): void {
  window.print();
}
